import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Loader2, Warehouse, Info } from "lucide-react";
import { api, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { GarageDetailDialog } from "@/components/app/GarageDetailDialog";

const Garage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailVehicle, setDetailVehicle] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vehicles/garage");
      setVehicles(data.vehicles || []);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!confirm("Remove this entry from your garage?")) return;
    try {
      await api.delete(`/vehicles/garage/${id}`);
      setVehicles((v) => v.filter((x) => x.id !== id));
      toast.success("Removed.");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || e.message);
    }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="panel p-10 flex items-center justify-center text-amber-brass font-mono">
          <Loader2 size={16} className="animate-spin mr-2" /> Loading garage…
        </div>
      );
    }
    if (vehicles.length === 0) {
      return (
        <div data-testid="garage-empty" className="panel p-10 text-center">
          <Warehouse size={40} className="mx-auto text-amber-brass mb-3" />
          <div className="font-display text-3xl uppercase text-cream">Empty Bay</div>
          <p className="text-secondary mt-2">Nothing filed yet. Start with a photo, VIN, or tag decode.</p>
        </div>
      );
    }
    return (
      <div data-testid="garage-list" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {vehicles.map((v, i) => (
          <GarageCard key={v.id || `entry-${i}`} v={v} onRemove={remove} onOpenDetails={() => setDetailVehicle(v)} />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-xs tracking-widest text-amber-brass uppercase">// Station 04</p>
          <h1 className="font-display text-5xl uppercase text-cream mt-1">The Garage</h1>
          <p className="text-secondary mt-2">Every vehicle you&apos;ve decoded, saved and organized.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/identify" className="ghost-btn">+ Photo ID</Link>
          <Link to="/vin" className="ghost-btn">+ VIN</Link>
          <Link to="/tag" className="chrome-btn">+ Tag</Link>
        </div>
      </div>
      {renderBody()}
      <GarageDetailDialog vehicle={detailVehicle} onClose={() => setDetailVehicle(null)} />
    </div>
  );
};

const renderThumb = (v) => {
  if (v.has_photo && v.photo_base64) {
    return (
      <img
        src={`data:${v.photo_mime || "image/jpeg"};base64,${v.photo_base64}`}
        alt=""
        className="w-full h-full object-cover"
      />
    );
  }
  if (v.vin) {
    return <div className="font-mono text-xs text-secondary p-3 break-all">VIN: {v.vin}</div>;
  }
  return <Warehouse size={32} className="text-secondary" />;
};

const kindLabel = (v) => {
  if (v.vin_decoded) return "VIN";
  if (v.photo_identified) return "PHOTO";
  if (v.tag_decoded) return "TAG";
  return "ENTRY";
};

const specialVariantOf = (v) =>
  v.photo_identified?.special_variant ||
  v.vin_decoded?.classic_decode?.decoded?.special_variant ||
  v.tag_decoded?.special_variant;

const GarageCard = ({ v, onRemove, onOpenDetails }) => {
  const variant = specialVariantOf(v);
  const hasDetails = Boolean(v.vin_decoded || v.tag_decoded || v.photo_identified || v.notes);
  return (
    <div data-testid="garage-item" className="panel overflow-hidden group">
      <div className="aspect-video bg-elevated border-b border-shop flex items-center justify-center relative">
        {renderThumb(v)}
        <span className="absolute top-2 left-2 tag-chip">{kindLabel(v)}</span>
      </div>
      <div className="p-4">
        <div className="font-display uppercase text-xl text-cream leading-tight">
          {v.year || "—"} {v.make || "Unknown"}
        </div>
        <div className="font-display uppercase text-lg text-red-candy leading-tight">
          {v.model || ""} {v.trim && <span className="text-amber-brass">— {v.trim}</span>}
        </div>
        {variant && (
          <div className="mt-2 inline-block border-l-2 border-[var(--accent-red)] bg-[rgba(217,32,39,0.1)] px-2 py-1">
            <span className="font-display uppercase tracking-wide text-sm text-cream">{variant}</span>
          </div>
        )}
        {v.vin && <div className="font-mono text-[10px] text-secondary mt-2 break-all">{v.vin}</div>}
        {v.confirmed_options?.length > 0 && (
          <div data-testid="garage-item-options" className="mt-3 flex flex-wrap gap-1.5">
            {v.confirmed_options.map((opt) => (
              <span
                key={`${v.id}-${opt}`}
                className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[var(--accent-amber)] text-amber-brass bg-elevated"
              >
                {opt}
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between mt-4 gap-2">
          <span className="font-mono text-[10px] text-secondary">
            {v.created_at ? new Date(v.created_at).toLocaleDateString() : ""}
          </span>
          <div className="flex items-center gap-1">
            <button
              data-testid={`garage-details-${v.id}`}
              onClick={onOpenDetails}
              className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-shop-strong text-amber-brass hover:border-[var(--accent-amber)] hover:bg-elevated flex items-center gap-1 transition-colors"
              title={hasDetails ? "View full VIN / tag / photo decode" : "Basic entry — no detailed decode saved"}
            >
              <Info size={11} /> Details
            </button>
            <button
              data-testid={`garage-delete-${v.id}`}
              onClick={() => onRemove(v.id)}
              className="text-secondary hover:text-red-candy transition-colors p-1"
              aria-label="delete"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Garage;
